import { animate, type MotionValue } from "framer-motion";
import { useCallback, useEffect, useRef } from "react";
import { useLocation } from "react-router";

import {
  pageDragX,
  contentDragX,
  imageDragX,
  isDragging,
  setDragging,
  setDragType,
  setPendingDragCleanup,
  setSkipNextTransition,
  type DragTier,
  type DragType,
} from "../components/layout/transition/dragOffset";
import { setTransitionDirection } from "../components/layout/transition/transitionDirection";
import { triggerTransitionHaptic } from "../utils/haptics";

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const DEAD_ZONE = 10;
const VERTICAL_TOLERANCE = 0.75;
const DEFAULT_THRESHOLD = 50;
const DEFAULT_VELOCITY_THRESHOLD = 0.3;
/** Pointer samples older than this (ms) do not count toward the release velocity. */
const VELOCITY_WINDOW = 100;

/** Fraction of viewport beyond which elastic dampening kicks in. */
const ELASTIC_THRESHOLD_RATIO = 0.5;
/** Movement multiplier beyond the elastic threshold. */
const ELASTIC_FACTOR = 0.3;

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type DragAction =
  { type: "navigate" } | { type: "subItem" } | { type: "rubberband" };

/** The pointer handlers a swipe surface spreads onto its element. */
export interface SwipeHandlers {
  onPointerDown: (e: React.PointerEvent) => void;
  onPointerMove: (e: React.PointerEvent) => void;
  onPointerUp: (e: React.PointerEvent) => void;
  onPointerCancel: (e: React.PointerEvent) => void;
}

export interface SwipeDragConfig {
  /**
   * Called whenever the gesture engages a side of its origin: at the first
   * horizontal movement, and again each time the finger crosses back over
   * the origin. Return null to suppress the drag for that direction.
   */
  resolve: (direction: "left" | "right") => DragAction | null;
  /**
   * Called when a drag gesture commits. `direction` is the side of the
   * origin the finger released on; `action` is what `resolve` returned for
   * it. The caller handles navigation or state updates.
   */
  onCommit: (action: DragAction, direction: "left" | "right") => void;
  /**
   * MotionValue and DragType for page-level navigation.
   * Defaults to { motionValue: pageDragX, dragType: "page" }.
   * ContentDetail passes { motionValue: contentDragX, dragType: "content" }.
   */
  pageLevel?: {
    motionValue: MotionValue<number>;
    dragType: "page" | "content";
  };
  /** Minimum horizontal distance (px) to commit. Default: 50 */
  threshold?: number;
  /** Minimum velocity (px/ms) for a fast-flick commit. Default: 0.3 */
  velocityThreshold?: number;
}

interface PointerSample {
  x: number;
  t: number;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Touch and pen swipe bare; a mouse needs Alt held, the desktop gate. */
function isAcceptedPointer(e: React.PointerEvent): boolean {
  // A second finger is never a gesture of its own.
  if (!e.isPrimary) return false;
  if (e.pointerType === "touch" || e.pointerType === "pen") return true;
  return e.pointerType === "mouse" && e.altKey;
}

/** Apply elastic dampening to a drag offset. */
function applyElastic(
  dx: number,
  fullElastic: boolean,
  containerW: number,
): number {
  if (fullElastic) return dx * ELASTIC_FACTOR;
  const threshold = containerW * ELASTIC_THRESHOLD_RATIO;
  const absDx = Math.abs(dx);
  if (absDx <= threshold) return dx;
  const excess = absDx - threshold;
  return Math.sign(dx) * (threshold + excess * ELASTIC_FACTOR);
}

/** Which side of its origin an offset is on; the origin itself is neither. */
function sideOf(dx: number): "left" | "right" | null {
  if (dx === 0) return null;
  return dx < 0 ? "left" : "right";
}

/**
 * Signed velocity (px/ms) over the window ending at `t`: the offset from
 * where the finger was when the window opened, with a finger that sent no
 * events counted as still. A gesture younger than the window is read whole.
 */
export function trailingVelocity(
  samples: PointerSample[],
  x: number,
  t: number,
): number {
  const since = t - VELOCITY_WINDOW;
  let anchor = samples[0];
  if (!anchor) return 0;
  for (const sample of samples) {
    if (sample.t > since) break;
    anchor = sample;
  }
  const span = Math.min(t - anchor.t, VELOCITY_WINDOW);
  if (span <= 0) return 0;
  return (x - anchor.x) / span;
}

export interface ReleaseInput {
  /** Offset from the origin at release (px, signed). */
  dx: number;
  /** Trailing velocity at release (px/ms, signed). */
  velocity: number;
  /** The side the gesture last engaged; `action` is what `resolve` returned for it. */
  side: "left" | "right";
  action: DragAction;
  threshold: number;
  velocityThreshold: number;
}

/**
 * The release decision. Two things commit, both judged on `side`: an offset
 * at or past `threshold`, or a velocity toward that side at or past
 * `velocityThreshold`. A flick back toward the origin that fast cancels
 * whatever the offset; a rubberband, a release on the origin, and a release
 * on a side the gesture never engaged all cancel.
 */
export function judgeRelease(input: ReleaseInput): "commit" | "cancel" {
  const { dx, velocity, side, action, threshold, velocityThreshold } = input;
  if (action.type === "rubberband" || sideOf(dx) !== side) return "cancel";
  const toward = side === "left" ? -velocity : velocity;
  if (toward <= -velocityThreshold) return "cancel";
  if (Math.abs(dx) >= threshold || toward >= velocityThreshold) return "commit";
  return "cancel";
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

/**
 * Pointer gestures for the carousel strips. A gesture has a side, the sign
 * of its offset from the origin, and `resolve` runs for whichever side the
 * finger is on; the release commits or cancels from the position and the
 * trailing velocity at that moment.
 *
 * One gesture at a time: a non-primary pointer is ignored, a pointer landing
 * on a bounce finishes it, and a pointer landing while a commit is on its
 * way to the router is ignored until the router has reset the strip.
 */
export function useSwipeDrag(config: SwipeDragConfig): SwipeHandlers {
  const configRef = useRef(config);
  configRef.current = config;
  // The location a commit navigates away from; the strip resets only once
  // the router has moved past it (see dragOffset's pending cleanup).
  const locationKey = useRef("");
  locationKey.current = useLocation().key;

  const startX = useRef(0);
  const startY = useRef(0);
  const samples = useRef<PointerSample[]>([]);
  const tracking = useRef(false);
  const decided = useRef(false);
  const pointerId = useRef<number | null>(null);
  const resolvedAction = useRef<DragAction | null>(null);
  /** The side of the origin the finger is on; `resolvedAction` is its action. */
  const side = useRef<"left" | "right" | null>(null);
  const activeType = useRef<DragType>(null);
  /** The MotionValue being driven during the current gesture. */
  const activeMV = useRef<MotionValue<number>>(pageDragX);
  /** Width of the container the swipe handlers are attached to. */
  const containerW = useRef(
    typeof window === "undefined" ? 0 : window.innerWidth,
  );
  /** The strip springing back to rest, until it settles or a pointer lands. */
  const bounce = useRef<{ mv: MotionValue<number> } | null>(null);

  const reset = useCallback(() => {
    tracking.current = false;
    decided.current = false;
    pointerId.current = null;
    resolvedAction.current = null;
    side.current = null;
    activeType.current = null;
  }, []);

  const settleAtRest = useCallback(() => {
    setDragging(false);
    setDragType(null);
  }, []);

  /** Spring the strip back; a pointer landing meanwhile finishes it at once. */
  const bounceBack = useCallback(
    (mv: MotionValue<number>) => {
      const current = { mv };
      bounce.current = current;
      animate(mv, 0, { type: "spring", stiffness: 400, damping: 30 }).then(
        () => {
          if (bounce.current !== current) return;
          bounce.current = null;
          settleAtRest();
        },
      );
    },
    [settleAtRest],
  );

  const finishBounce = useCallback(() => {
    const current = bounce.current;
    if (!current) return;
    bounce.current = null;
    current.mv.stop();
    current.mv.set(0);
    settleAtRest();
  }, [settleAtRest]);

  /** Drop a gesture whose pointer never reported its end. */
  const abandon = useCallback(() => {
    if (activeType.current) {
      activeMV.current.set(0);
      settleAtRest();
    }
    reset();
  }, [reset, settleAtRest]);

  // An unmount mid-gesture or mid-bounce would otherwise leave the shared
  // drag state claiming a gesture nobody can finish. A commit in flight is
  // the router's to reset.
  useEffect(
    () => () => {
      if (tracking.current) abandon();
      finishBounce();
    },
    [abandon, finishBounce],
  );

  /** Resolve `direction` and point the gesture at its action's strip. */
  const engage = useCallback((direction: "left" | "right"): boolean => {
    const action = configRef.current.resolve(direction);
    if (!action) return false;

    // Determine DragType and MotionValue based on action + pageLevel config
    const pageLevel = configRef.current.pageLevel;
    const pageMV = pageLevel?.motionValue ?? pageDragX;
    const pageDT = pageLevel?.dragType ?? "page";

    let type: DragType;
    let mv: MotionValue<number>;
    if (action.type === "subItem") {
      type = "image";
      mv = imageDragX;
    } else if (action.type === "navigate") {
      type = pageDT;
      mv = pageMV;
    } else {
      // rubberband
      type = "rubberband";
      mv = pageMV;
    }

    side.current = direction;
    resolvedAction.current = action;
    activeType.current = type;
    activeMV.current = mv;
    setDragType(type);
    return true;
  }, []);

  const onPointerDown = useCallback(
    (e: React.PointerEvent) => {
      if (!isAcceptedPointer(e)) return;
      if (tracking.current) abandon();
      if (bounce.current) {
        finishBounce();
      } else if (isDragging()) {
        // A commit is on its way to the router; the strip shows the
        // destination until the router's commit render resets it.
        return;
      }

      startX.current = e.clientX;
      startY.current = e.clientY;
      samples.current = [{ x: e.clientX, t: e.timeStamp }];
      tracking.current = true;
      decided.current = false;
      pointerId.current = e.pointerId;
      resolvedAction.current = null;
      side.current = null;
      activeType.current = null;

      containerW.current = (e.currentTarget as HTMLElement).offsetWidth;
      (e.target as HTMLElement).setPointerCapture(e.pointerId);
    },
    [abandon, finishBounce],
  );

  const onPointerMove = useCallback(
    (e: React.PointerEvent) => {
      if (!tracking.current || e.pointerId !== pointerId.current) return;

      const dx = e.clientX - startX.current;
      const absDx = Math.abs(dx);
      const dy = Math.abs(e.clientY - startY.current);

      const recent = samples.current;
      recent.push({ x: e.clientX, t: e.timeStamp });
      // Keep one sample from before the window: where the finger was when it opened.
      while (recent.length > 1 && recent[1].t <= e.timeStamp - VELOCITY_WINDOW)
        recent.shift();

      // ---- Axis lock (once) ----
      if (!decided.current) {
        if (absDx + dy < DEAD_ZONE) return;
        decided.current = true;

        const isHorizontal = absDx > 0 && dy / absDx <= VERTICAL_TOLERANCE;
        if (!isHorizontal) {
          // Vertical gesture — bail out entirely.
          tracking.current = false;
          return;
        }

        if (!engage(dx < 0 ? "left" : "right")) {
          // Caller says no drag for this direction.
          tracking.current = false;
          return;
        }

        setDragging(true);
        // Reset all MotionValues to ensure clean start
        pageDragX.set(0);
        contentDragX.set(0);
        imageDragX.set(0);
        return;
      }

      // ---- Continuous drag update ----
      if (!resolvedAction.current || !side.current) return;

      // Crossing the origin re-resolves; every strip is at rest here, so a
      // change of strip is invisible.
      const newSide = sideOf(dx);
      if (newSide && newSide !== side.current) {
        activeMV.current.set(0);
        if (!engage(newSide)) {
          // Nothing to drag on this side: the gesture ends at rest.
          reset();
          settleAtRest();
          return;
        }
      }

      const isRubberband = resolvedAction.current.type === "rubberband";
      const visualDx = applyElastic(dx, isRubberband, containerW.current);
      activeMV.current.set(visualDx);
    },
    [engage, reset, settleAtRest],
  );

  const onPointerUp = useCallback(
    (e: React.PointerEvent) => {
      if (!tracking.current || e.pointerId !== pointerId.current) return;

      const action = resolvedAction.current;
      const direction = side.current;
      const type = activeType.current;
      const mv = activeMV.current;
      const dx = e.clientX - startX.current;
      const velocity = trailingVelocity(
        samples.current,
        e.clientX,
        e.timeStamp,
      );

      reset();

      // If we never engaged a side, nothing to do.
      if (!action || !direction || !type) return;

      const {
        threshold = DEFAULT_THRESHOLD,
        velocityThreshold = DEFAULT_VELOCITY_THRESHOLD,
      } = configRef.current;

      const verdict = judgeRelease({
        dx,
        velocity,
        side: direction,
        action,
        threshold,
        velocityThreshold,
      });

      if (verdict === "commit") {
        // Fire the transition haptic synchronously — still inside the
        // pointerup handler, so the Vibration API retains user activation.
        // page & content commits are "heavy"; sub-item (image) is "medium".
        if (type === "page" || type === "content" || type === "image") {
          triggerTransitionHaptic(type);
        }

        // Animate to the edge of the container, not the full viewport.
        const targetWidth = containerW.current;
        const target = direction === "left" ? -targetWidth : targetWidth;

        animate(mv, target, {
          type: "tween",
          duration: 0.2,
          ease: "easeOut",
        }).then(() => {
          if (action.type === "navigate") {
            setSkipNextTransition();
            setTransitionDirection(direction);
          }

          // Defer strip reset + preview teardown until the destination has
          // committed; the strip's useDragCommitCleanup resolves both in
          // one paint.
          setPendingDragCleanup(mv, type as DragTier, locationKey.current);

          configRef.current.onCommit(action, direction);
        });
      } else {
        bounceBack(mv);
      }
    },
    [bounceBack, reset],
  );

  const onPointerCancel = useCallback(
    (e: React.PointerEvent) => {
      if (e.pointerId !== pointerId.current) return;

      const mv = activeMV.current;
      const type = activeType.current;
      reset();

      if (type) bounceBack(mv);
    },
    [bounceBack, reset],
  );

  return { onPointerDown, onPointerMove, onPointerUp, onPointerCancel };
}
